import { Injectable, inject, signal } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MatDateRangeSelectionStrategy
} from '@angular/material/datepicker';
import { Subject } from 'rxjs';

/** Local midnight today — the fixed end of a locked range. */
export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Range selection for the reports whose window always ends on the current date.
 *
 * While locked, a click in the calendar sets the start and the end snaps to
 * today, so the range is complete after one click and the calendar never offers
 * an end to pick. Today is recomputed per selection, so a tab left open
 * overnight picks up the new date.
 *
 * Provided per filter bar and switched on by the bar from its own input: an
 * unlocked bar keeps Material's default two-click behaviour untouched.
 */
@Injectable()
export class FixedEndDateRangeStrategy implements MatDateRangeSelectionStrategy<Date> {
  private readonly adapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly fallback = new DefaultMatCalendarRangeStrategy<Date>(this.adapter);
  private readonly dateFormats = inject(MAT_DATE_FORMATS);

  /** A message each time a selection is corrected — the filter bar shows it. */
  readonly corrections = new Subject<string>();

  /** Set by the filter bar once its inputs are bound. */
  locked = false;

  /** Longest range allowed, in calendar months, or null for no limit. Set by the filter bar. */
  maxRangeMonths: number | null = null;

  /**
   * Whether the picker's pending range has both ends — what the Apply button
   * keys off.
   *
   * Picker actions make Material clone the selection model, so the pending
   * range is invisible to the input and to the form until Apply commits it.
   * This strategy sits on the one hook that sees every calendar selection,
   * which makes it the only place that can answer the question.
   */
  readonly complete = signal(true);

  selectionFinished(date: Date | null, currentRange: DateRange<Date>): DateRange<Date> {
    const range = this.locked
      // A cleared selection leaves the range as it was rather than emptying it
      // — these reports have no meaningful "no dates" state.
      ? (date ? new DateRange(date, startOfToday()) : currentRange)
      : this.capWithNotice(this.fallback.selectionFinished(date!, currentRange));

    this.complete.set(range.start != null && range.end != null);

    return range;
  }

  createPreview(activeDate: Date | null, currentRange: DateRange<Date>): DateRange<Date> {
    // Locked, the range is never half-selected, so the default already draws no
    // preview. Delegating keeps both paths on one code path. Capped like the
    // selection, so the hover highlight stops where the range would — the
    // limit is visible before the second click, not after it.
    return this.capSpan(this.fallback.createPreview(activeDate, currentRange));
  }

  createDrag(dragOrigin: Date, originalRange: DateRange<Date>, newDate: Date): DateRange<Date> | null {
    // Dragging the end handle is the one remaining way to move the end, so drop
    // the gesture while locked. Null is what the default returns for a drag it
    // won't honour — also used for a drag that would stretch past the limit or
    // onto a future date.
    if (this.locked) {
      return null;
    }
    const dragged = this.fallback.createDrag(dragOrigin, originalRange, newDate);
    return dragged && (this.capSpan(dragged) !== dragged || this.isFuture(dragged.end))
      ? null
      : dragged;
  }

  /** capSpan, announcing it when it moved the end: the picked range was longer than allowed. */
  private capWithNotice(range: DateRange<Date>): DateRange<Date> {
    const capped = this.capSpan(range);
    if (capped !== range) {
      const end = this.adapter.format(capped.end!, this.dateFormats.display.dateInput);
      this.corrections.next(
        `Date ranges are limited to ${this.maxRangeMonths} months, so the end date was set to ${end}.`);
    }
    return capped;
  }

  private isFuture(date: Date | null): boolean {
    return date != null && this.adapter.compareDate(date, startOfToday()) > 0;
  }

  /** The range with an end past the allowed span pulled back to the last day it permits. */
  private capSpan(range: DateRange<Date>): DateRange<Date> {
    if (this.maxRangeMonths == null || !range.start || !range.end) {
      return range;
    }
    // Inclusive of both ends: 12 months from Oct 19 runs through Oct 18.
    const lastAllowed = this.adapter.addCalendarDays(
      this.adapter.addCalendarMonths(range.start, this.maxRangeMonths), -1);
    return this.adapter.compareDate(range.end, lastAllowed) > 0
      ? new DateRange(range.start, lastAllowed)
      : range;
  }
}
